import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import {
  formControlClass,
  formErrorBannerClass,
  formInputClass,
  formLabelClass,
  formPrimaryButtonClass,
  formSuccessBannerClass,
  formTextareaClass,
  pageCanvasClass,
  surfaceCardClass,
} from '../components/ui/formUiClasses';

const DSE_GRADES = ['5**', '5*', '5', '4', '3', '2', '1'];
const CSD_GRADES = ['Attained', 'Unattained'];
const INSTITUTIONS = ['HKU', 'CUHK', 'HKUST', 'CityU', 'Other'];
const HELPFULNESS = [1, 2, 3, 4, 5];

const EMPTY_FORM = {
  jupasCode: '',
  institution: '',
  institutionOther: '',
  dseChinese: '',
  dseEnglish: '',
  dseMath: '',
  dseCsd: '',
  dseElectives: '',
  offerRanking: '',
  spnBonus: '',
  spnBonusDetails: '',
  jupasRanking: '',
  usedCalculator: '',
  calculatorHelpfulness: '',
  usedInterestTest: '',
  interestHelpfulness: '',
  interestAccuracy: '',
  improvements: '',
  freeformFeedback: '',
  volunteerWilling: false,
  volunteerName: '',
  volunteerProgramme: '',
  volunteerContact: '',
};

function mapResponseToForm(response) {
  if (!response) return { ...EMPTY_FORM };
  return {
    jupasCode: response.jupasCode || '',
    institution: response.institution || '',
    institutionOther: response.institutionOther || '',
    dseChinese: response.dseChinese || '',
    dseEnglish: response.dseEnglish || '',
    dseMath: response.dseMath || '',
    dseCsd: response.dseCsd || '',
    dseElectives: response.dseElectives || '',
    offerRanking: response.offerRanking || '',
    spnBonus: response.spnBonus || '',
    spnBonusDetails: response.spnBonusDetails || '',
    jupasRanking: response.jupasRanking || '',
    usedCalculator: response.usedCalculator || '',
    calculatorHelpfulness: response.calculatorHelpfulness ?? '',
    usedInterestTest: response.usedInterestTest || '',
    interestHelpfulness: response.interestHelpfulness ?? '',
    interestAccuracy: response.interestAccuracy ?? '',
    improvements: response.improvements || '',
    freeformFeedback: response.freeformFeedback || '',
    volunteerWilling: Boolean(response.volunteerWilling),
    volunteerName: response.volunteerName || '',
    volunteerProgramme: response.volunteerProgramme || '',
    volunteerContact: response.volunteerContact || '',
  };
}

function prefillFromUser(user) {
  const scores = user?.dse_scores && typeof user.dse_scores === 'object' ? user.dse_scores : {};
  const name =
    [user?.Nickname, user?.First_Name, user?.Last_Name].filter(Boolean).join(' ').trim() ||
    user?.User_ID ||
    '';

  const electives = Object.entries(scores)
    .filter(([code]) => !['CHI', 'ENG', 'MAT', 'CSD'].includes(code))
    .map(([code, score]) => `${code} ${score}`)
    .join(', ');

  return {
    dseChinese: scores.CHI || '',
    dseEnglish: scores.ENG || '',
    dseMath: scores.MAT || '',
    dseCsd: scores.CSD === 'A' || scores.CSD === 'Attained' ? 'Attained' : scores.CSD || '',
    dseElectives: electives,
    volunteerName: name,
  };
}

function FieldLabel({ children, required }) {
  return (
    <label className={formLabelClass}>
      {children}
      {required ? <span className="text-red-600"> *</span> : null}
    </label>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className={`${surfaceCardClass} p-5 sm:p-6 space-y-4`}>
      <div>
        <h2 className="text-lg font-semibold text-ink">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-ink-muted whitespace-pre-line">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

function YesNoField({ name, value, onChange, yesLabel, noLabel }) {
  return (
    <div className="mt-1 flex flex-wrap gap-4">
      {[
        { v: 'yes', label: yesLabel },
        { v: 'no', label: noLabel },
      ].map((opt) => (
        <label key={opt.v} className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
          <input
            type="radio"
            name={name}
            value={opt.v}
            checked={value === opt.v}
            onChange={onChange}
            className="h-4 w-4 border-edge-strong text-primary focus:ring-ring"
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function ScaleField({ name, value, onChange, labels }) {
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-2">
        {HELPFULNESS.map((n) => (
          <label
            key={n}
            className={`inline-flex h-10 min-w-10 cursor-pointer items-center justify-center rounded-lg border px-3 text-sm font-medium transition ${
              String(value) === String(n)
                ? 'border-link bg-accent/25 text-link'
                : 'border-edge-strong bg-surface-muted text-ink hover:bg-surface'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={n}
              checked={String(value) === String(n)}
              onChange={onChange}
              className="sr-only"
            />
            {n}
          </label>
        ))}
      </div>
      <div className="flex justify-between gap-2 text-xs text-ink-muted">
        <span>{labels.low}</span>
        <span>{labels.high}</span>
      </div>
    </div>
  );
}

function JupasSurvey() {
  const { t } = useTranslation();
  const { currentUser, isAuthenticated } = useAuth();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasExisting, setHasExisting] = useState(false);

  const accountHint = useMemo(() => {
    if (isAuthenticated && currentUser?.User_ID) {
      return t('jupasSurvey.linkedAccount', { id: currentUser.User_ID });
    }
    return t('jupasSurvey.anonymousHint');
  }, [isAuthenticated, currentUser?.User_ID, t]);

  const loadExisting = useCallback(async () => {
    if (!isAuthenticated) return false;
    const res = await apiClient.get('/api/jupas-survey/me');
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || t('jupasSurvey.loadFailed'));
    if (data.response) {
      setForm(mapResponseToForm(data.response));
      setHasExisting(true);
      return true;
    }
    return false;
  }, [isAuthenticated, t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const found = await loadExisting();
        if (!cancelled && !found && isAuthenticated) {
          setForm((prev) => ({ ...prev, ...prefillFromUser(currentUser) }));
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isAuthenticated, loadExisting]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateClient = () => {
    if (!form.jupasCode.trim()) return t('jupasSurvey.errors.jupasCode');
    if (!form.institution) return t('jupasSurvey.errors.institution');
    if (form.institution === 'Other' && !form.institutionOther.trim()) {
      return t('jupasSurvey.errors.institutionOther');
    }
    if (!form.dseChinese || !form.dseEnglish || !form.dseMath || !form.dseCsd) {
      return t('jupasSurvey.errors.coreScores');
    }
    if (!form.dseElectives.trim()) return t('jupasSurvey.errors.electives');
    if (!form.offerRanking.trim()) return t('jupasSurvey.errors.offerRanking');
    if (!form.spnBonus) return t('jupasSurvey.errors.spnBonus');
    if (!form.jupasRanking.trim()) return t('jupasSurvey.errors.jupasRanking');
    if (!form.usedCalculator) return t('jupasSurvey.errors.usedCalculator');
    if (form.usedCalculator === 'yes' && !form.calculatorHelpfulness) {
      return t('jupasSurvey.errors.calculatorHelpfulness');
    }
    if (!form.usedInterestTest) return t('jupasSurvey.errors.usedInterestTest');
    if (form.usedInterestTest === 'yes' && (!form.interestHelpfulness || !form.interestAccuracy)) {
      return t('jupasSurvey.errors.interestFeedback');
    }
    if (form.volunteerWilling && (!form.volunteerName.trim() || !form.volunteerContact.trim())) {
      return t('jupasSurvey.errors.volunteer');
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientError = validateClient();
    if (clientError) {
      setError(clientError);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        calculatorHelpfulness: form.calculatorHelpfulness === '' ? null : Number(form.calculatorHelpfulness),
        interestHelpfulness: form.interestHelpfulness === '' ? null : Number(form.interestHelpfulness),
        interestAccuracy: form.interestAccuracy === '' ? null : Number(form.interestAccuracy),
      };
      const res = await apiClient.post('/api/jupas-survey', payload);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('jupasSurvey.submitFailed'));

      if (data.anonymous) {
        setHasExisting(false);
        setForm({ ...EMPTY_FORM });
        setSuccess(t('jupasSurvey.submitSuccessAnonymous'));
      } else {
        setForm(mapResponseToForm(data.response));
        setHasExisting(true);
        setSuccess(t('jupasSurvey.submitSuccess'));
      }
    } catch (err) {
      setError(err.message || t('jupasSurvey.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`${pageCanvasClass} min-h-screen`}>
        <div className="mx-auto max-w-3xl px-4 py-10">
          <p className="text-ink-muted">{t('jupasSurvey.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${pageCanvasClass} min-h-screen`}>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10 space-y-6">
        <header className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-ink">{t('jupasSurvey.title')}</h1>
            <span className="rounded-full border border-edge-strong bg-surface-muted px-2.5 py-1 text-xs font-semibold text-ink-muted">
              {t('jupasSurvey.legacyBadge')}
            </span>
          </div>
          <p className="text-sm sm:text-base text-ink-muted whitespace-pre-line">{t('jupasSurvey.introZh')}</p>
          <p className="text-sm sm:text-base text-ink-muted whitespace-pre-line">{t('jupasSurvey.introEn')}</p>
          {accountHint ? (
            <p className="text-xs text-ink-subtle">{accountHint}</p>
          ) : null}
          {hasExisting ? (
            <p className="text-sm text-link">{t('jupasSurvey.alreadySubmitted')}</p>
          ) : null}
        </header>

        {error ? <div className={formErrorBannerClass}>{error}</div> : null}
        {success ? <div className={formSuccessBannerClass}>{success}</div> : null}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Section title={t('jupasSurvey.sections.offer')}>
            <div>
              <FieldLabel required>{t('jupasSurvey.fields.jupasCode')}</FieldLabel>
              <p className="mt-0.5 text-xs text-ink-muted">{t('jupasSurvey.hints.jupasCode')}</p>
              <input
                name="jupasCode"
                value={form.jupasCode}
                onChange={handleChange}
                className={formInputClass}
                placeholder="JS6054"
                maxLength={20}
                autoComplete="off"
              />
            </div>

            <div>
              <FieldLabel required>{t('jupasSurvey.fields.institution')}</FieldLabel>
              <select
                name="institution"
                value={form.institution}
                onChange={handleChange}
                className={`mt-1 ${formControlClass}`}
              >
                <option value="">{t('jupasSurvey.selectPlaceholder')}</option>
                {INSTITUTIONS.map((code) => (
                  <option key={code} value={code}>
                    {t(`jupasSurvey.institutions.${code}`)}
                  </option>
                ))}
              </select>
              {form.institution === 'Other' ? (
                <input
                  name="institutionOther"
                  value={form.institutionOther}
                  onChange={handleChange}
                  className={`${formInputClass} mt-2`}
                  placeholder={t('jupasSurvey.fields.institutionOther')}
                  maxLength={120}
                />
              ) : null}
            </div>

            <div>
              <FieldLabel required>{t('jupasSurvey.fields.offerRanking')}</FieldLabel>
              <p className="mt-0.5 text-xs text-ink-muted">{t('jupasSurvey.hints.offerRanking')}</p>
              <input
                name="offerRanking"
                value={form.offerRanking}
                onChange={handleChange}
                className={formInputClass}
                placeholder="A1"
                maxLength={40}
              />
            </div>
          </Section>

          <Section title={t('jupasSurvey.sections.coreScores')} subtitle={t('jupasSurvey.hints.coreScores')}>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { name: 'dseChinese', label: t('jupasSurvey.fields.chinese'), options: DSE_GRADES },
                { name: 'dseEnglish', label: t('jupasSurvey.fields.english'), options: DSE_GRADES },
                { name: 'dseMath', label: t('jupasSurvey.fields.math'), options: DSE_GRADES },
                { name: 'dseCsd', label: t('jupasSurvey.fields.csd'), options: CSD_GRADES },
              ].map((field) => (
                <div key={field.name}>
                  <FieldLabel required>{field.label}</FieldLabel>
                  <select
                    name={field.name}
                    value={form[field.name]}
                    onChange={handleChange}
                    className={`mt-1 ${formControlClass}`}
                  >
                    <option value="">{t('jupasSurvey.selectPlaceholder')}</option>
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div>
              <FieldLabel required>{t('jupasSurvey.fields.electives')}</FieldLabel>
              <p className="mt-0.5 text-xs text-ink-muted">{t('jupasSurvey.hints.electives')}</p>
              <input
                name="dseElectives"
                value={form.dseElectives}
                onChange={handleChange}
                className={formInputClass}
                placeholder="bio 5, chem 4, econ 3"
                maxLength={500}
              />
            </div>
          </Section>

          <Section title={t('jupasSurvey.sections.ranking')}>
            <div>
              <FieldLabel required>{t('jupasSurvey.fields.spnBonus')}</FieldLabel>
              <YesNoField
                name="spnBonus"
                value={form.spnBonus}
                onChange={handleChange}
                yesLabel={t('jupasSurvey.yes')}
                noLabel={t('jupasSurvey.no')}
              />
              {form.spnBonus === 'yes' ? (
                <input
                  name="spnBonusDetails"
                  value={form.spnBonusDetails}
                  onChange={handleChange}
                  className={`${formInputClass} mt-2`}
                  placeholder={t('jupasSurvey.fields.spnBonusDetails')}
                  maxLength={500}
                />
              ) : null}
            </div>

            <div>
              <FieldLabel required>{t('jupasSurvey.fields.jupasRanking')}</FieldLabel>
              <p className="mt-0.5 text-xs text-ink-muted">{t('jupasSurvey.hints.jupasRanking')}</p>
              <textarea
                name="jupasRanking"
                value={form.jupasRanking}
                onChange={handleChange}
                className={formTextareaClass}
                rows={4}
                maxLength={4000}
                placeholder="A1 hku bba ; A2 hku ba ; A3 cityu marketing"
              />
            </div>
          </Section>

          <Section title={t('jupasSurvey.sections.edvise')}>
            <div>
              <FieldLabel required>{t('jupasSurvey.fields.usedCalculator')}</FieldLabel>
              <YesNoField
                name="usedCalculator"
                value={form.usedCalculator}
                onChange={handleChange}
                yesLabel={t('jupasSurvey.yes')}
                noLabel={t('jupasSurvey.no')}
              />
            </div>

            {form.usedCalculator === 'yes' ? (
              <div>
                <FieldLabel required>{t('jupasSurvey.fields.calculatorHelpfulness')}</FieldLabel>
                <ScaleField
                  name="calculatorHelpfulness"
                  value={form.calculatorHelpfulness}
                  onChange={handleChange}
                  labels={{
                    low: t('jupasSurvey.scale.low'),
                    high: t('jupasSurvey.scale.high'),
                  }}
                />
              </div>
            ) : null}

            <div>
              <FieldLabel required>{t('jupasSurvey.fields.usedInterestTest')}</FieldLabel>
              <YesNoField
                name="usedInterestTest"
                value={form.usedInterestTest}
                onChange={handleChange}
                yesLabel={t('jupasSurvey.yes')}
                noLabel={t('jupasSurvey.no')}
              />
            </div>

            {form.usedInterestTest === 'yes' ? (
              <>
                <div>
                  <FieldLabel required>{t('jupasSurvey.fields.interestHelpfulness')}</FieldLabel>
                  <ScaleField
                    name="interestHelpfulness"
                    value={form.interestHelpfulness}
                    onChange={handleChange}
                    labels={{
                      low: t('jupasSurvey.scale.low'),
                      high: t('jupasSurvey.scale.high'),
                    }}
                  />
                </div>
                <div>
                  <FieldLabel required>{t('jupasSurvey.fields.interestAccuracy')}</FieldLabel>
                  <ScaleField
                    name="interestAccuracy"
                    value={form.interestAccuracy}
                    onChange={handleChange}
                    labels={{
                      low: t('jupasSurvey.scale.accuracyLow'),
                      high: t('jupasSurvey.scale.accuracyHigh'),
                    }}
                  />
                </div>
              </>
            ) : null}

            <div>
              <FieldLabel>{t('jupasSurvey.fields.improvements')}</FieldLabel>
              <textarea
                name="improvements"
                value={form.improvements}
                onChange={handleChange}
                className={formTextareaClass}
                rows={3}
                maxLength={4000}
              />
            </div>

            <div>
              <FieldLabel>{t('jupasSurvey.fields.freeform')}</FieldLabel>
              <textarea
                name="freeformFeedback"
                value={form.freeformFeedback}
                onChange={handleChange}
                className={formTextareaClass}
                rows={3}
                maxLength={4000}
              />
            </div>
          </Section>

          <Section title={t('jupasSurvey.sections.volunteer')} subtitle={t('jupasSurvey.volunteerBody')}>
            <label className="inline-flex items-start gap-2 text-sm text-ink cursor-pointer">
              <input
                type="checkbox"
                name="volunteerWilling"
                checked={form.volunteerWilling}
                onChange={handleChange}
                className="mt-1 h-4 w-4 rounded border-edge-strong text-primary focus:ring-ring"
              />
              <span>{t('jupasSurvey.fields.volunteerWilling')}</span>
            </label>

            {form.volunteerWilling ? (
              <div className="space-y-4 pt-2">
                <div>
                  <FieldLabel required>{t('jupasSurvey.fields.volunteerName')}</FieldLabel>
                  <input
                    name="volunteerName"
                    value={form.volunteerName}
                    onChange={handleChange}
                    className={formInputClass}
                    maxLength={120}
                  />
                </div>
                <div>
                  <FieldLabel>{t('jupasSurvey.fields.volunteerProgramme')}</FieldLabel>
                  <input
                    name="volunteerProgramme"
                    value={form.volunteerProgramme}
                    onChange={handleChange}
                    className={formInputClass}
                    maxLength={300}
                  />
                </div>
                <div>
                  <FieldLabel required>{t('jupasSurvey.fields.volunteerContact')}</FieldLabel>
                  <input
                    name="volunteerContact"
                    value={form.volunteerContact}
                    onChange={handleChange}
                    className={formInputClass}
                    placeholder={t('jupasSurvey.hints.volunteerContact')}
                    maxLength={300}
                  />
                </div>
              </div>
            ) : null}
          </Section>

          <div className="flex flex-wrap items-center gap-3 pb-8">
            <button type="submit" disabled={submitting} className={formPrimaryButtonClass}>
              {submitting
                ? t('common.loading')
                : hasExisting
                  ? t('jupasSurvey.update')
                  : t('jupasSurvey.submit')}
            </button>
            <p className="text-xs text-ink-muted">{t('jupasSurvey.privacyNote')}</p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default JupasSurvey;
